import { Component, inject, signal, WritableSignal } from '@angular/core';
import { ChubbHeaderComponent } from '../../components/chubb-header/chubb-header.component';
import { ChubbTopbarComponent } from '../../components/chubb-topbar/chubb-topbar.component';
import { ChubbChartPieComponent, ChubbChartPieData } from '../../components/chubb-chart-pie/chubb-chart-pie.component';
import { ChubbChartBarComponent, ChubbChartBarData } from '../../components/chubb-chart-bar/chubb-chart-bar.component';
import { ChubbCardComponent } from '../../components/chubb-card/chubb-card.component';
import { CommonModule } from '@angular/common';
import { ChubbTabsComponent } from '../../components/chubb-tabs/chubb-tabs.component';
import { FormsModule } from '@angular/forms';
import { CbCalendar } from '@chubb/ui-components/cb-calendar';
import { CbSelectChoiceModule } from '@chubb/ui-components/cb-select-choice';
import { SelectModule } from 'primeng/select';
import {
  COLUMNS,
  NO_REQUIREMENTS_TEXT,
  NO_RESULTS_FOUND_TEXT,
} from '../../shared/consts/dashboard.const';
import { CbIconModule } from '@chubb/ui-components';
import { Router } from '@angular/router';
import { DataService } from '../../services/data.service';
import { AdviserApiService } from '../../services/adviser-api.service';
import { ReferenceDataService } from '../../services/reference-data.service';
import { DashboardDetails } from '../../shared/models/dashboard-details.model';
import { BriefPolicy } from '../../shared/models/brief-policy.model';

@Component({
  selector: 'app-dashboard',
  imports: [
    ChubbHeaderComponent,
    ChubbTopbarComponent,
    ChubbCardComponent,
    ChubbChartPieComponent,
    ChubbChartBarComponent,
    CommonModule,
    FormsModule,
    ChubbTabsComponent,
    CbCalendar,
    CbSelectChoiceModule,
    SelectModule,
    CbIconModule
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard {

  currentIndex = 0;
  startX = 0;
  totalSlides = 3;
  columns = COLUMNS;
  searchTerm: string = '';
  viewType: string = 'table';
  showProgressbar: boolean = false;
  activeTabIndex: WritableSignal<number> = signal(0);
  initialLoading: WritableSignal<boolean> = signal(true);
  totalPolicies: WritableSignal<number> = signal(0);
  loadedPolicies: WritableSignal<number> = signal(0);
  policies: WritableSignal<BriefPolicy[]> = signal([]);
  filteredPolicies: WritableSignal<BriefPolicy[]> = signal([]);
  protected readonly _dataService: DataService = inject(DataService);
  readonly adviserApiService = inject(AdviserApiService);
  readonly referenceDataService = inject(ReferenceDataService);


  tabLabels: Array<{ value: string; label: string; shortLabel?: string }> = [
    { value: 'all', label: 'All' },
    { value: 'anniversary', label: 'Upcoming anniversaries (next 6 weeks)', shortLabel: 'Anniversary' },
    { value: 'arrears', label: 'In arrears' }
  ];

  piechartData: Array<ChubbChartPieData> = [
    { label: '1 week',  color: '#150F96', value: 30 },
    { label: '2 weeks', color: '#21369D', value: 25 },
    { label: '3 weeks', color: '#556AAB', value: 20 },
    { label: '4 weeks', color: '#ADB7CD', value: 15 },
    { label: '5 weeks', color: '#B8C2CF', value: 3,  patternId: 'pie-pattern-dots' },
    { label: '6 weeks', color: '#C8D4DE', value: 3,  patternId: 'pie-pattern-diagonal' },
  ];

  barchartData: Array<ChubbChartBarData> = [
    { label: '1', color: '#FFB617', value: 5 },
    { label: '2', color: '#FA6700', value: 8 },
    { label: '3+', color: '#AA010E', value: 5 },
  ];

  constructor(
    private readonly router: Router
  ) { }

  ngOnInit(): void {
      const userInfo = JSON.parse(sessionStorage.getItem('userinfo') ?? '{}');
      const adviserCode = 'GFSHSR';//userInfo?.custom_attributes?.AdviserCode;
      if (!adviserCode) {
        this.router.navigate(['error']);
        return;
      }
      this._dataService.set('adviserCode', adviserCode);
      this._getPolicies(adviserCode);
  }

  private readonly _mockDetails: DashboardDetails = {
    items: [
      { policy: { number: '312045', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '487621', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '563819', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '298734', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '741256', statusCode: 'IF',  adviserCode: 'KWADV1' } },
      { policy: { number: '634891', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '519023', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '867342', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '423078', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '756190', statusCode: 'IF',  adviserCode: 'KWADV1' } },
      { policy: { number: '381264', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '614937', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '208561', statusCode: 'PIF', adviserCode: 'KWADV1' } },
      { policy: { number: '935047', statusCode: 'IF',  adviserCode: 'KWADV1' } },
      { policy: { number: '470823', statusCode: 'PIF', adviserCode: 'KWADV1' } },
    ]
  };

  private readonly _mockBriefs: Record<string, BriefPolicy> = {
    '312045': { policy: { number: '312045', statusCode: 'PIF', productName: 'Life Cover Plus', lifeAssuredFullName: 'Aroha Tane', pendingApiInclGst: 79512, pendingAgeDays: 51, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 0, applicationReceivedOn: '2026-02-02T00:01:00+13:00', issuedOn: undefined, arrears: '02/02/2026', anniversary: '01/04/2026' } },
    '487621': { policy: { number: '487621', statusCode: 'PIF', productName: 'Life Cover Plus', lifeAssuredFullName: 'Ben Ngata, Sue Hemi', pendingApiInclGst: 351208, pendingAgeDays: 134, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 30394, applicationReceivedOn: '2025-11-11T00:01:00+13:00', issuedOn: undefined, arrears: '11/11/2025', anniversary: '11/11/2026' } },
    '563819': { policy: { number: '563819', statusCode: 'PIF', productName: 'Life Cover Plus', lifeAssuredFullName: 'Mere Parata', pendingApiInclGst: 60060, pendingAgeDays: 51, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 0, applicationReceivedOn: '2026-02-02T00:01:00+13:00', issuedOn: undefined, arrears: '02/02/2026', anniversary: '15/04/2026' } },
    '298734': { policy: { number: '298734', statusCode: 'PIF', productName: 'Life Cover Plus', lifeAssuredFullName: 'Jack Millar', pendingApiInclGst: 55068, pendingAgeDays: 147, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 2444, applicationReceivedOn: '2025-10-29T00:01:00+13:00', issuedOn: undefined, arrears: '29/10/2025', anniversary: '29/10/2026' } },
    '634891': { policy: { number: '634891', statusCode: 'PIF', productName: 'Life Cover Plus', lifeAssuredFullName: 'Tom Reweti', pendingApiInclGst: 127944, pendingAgeDays: 114, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 8976, applicationReceivedOn: '2025-12-01T00:01:00+13:00', issuedOn: undefined, arrears: '01/12/2025', anniversary: '01/12/2026' } },
    '519023': { policy: { number: '519023', statusCode: 'PIF', productName: 'Business Life Cover', lifeAssuredFullName: 'Lisa Reweti', pendingApiInclGst: 169080, pendingAgeDays: 114, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 18612, applicationReceivedOn: '2025-12-01T00:01:00+13:00', issuedOn: undefined, arrears: '01/12/2025', anniversary: '28/04/2026' } },
    '741256': { policy: { number: '741256', statusCode: 'IF',  productName: 'Life Cover Plus', lifeAssuredFullName: 'Hemi Tuhou', pendingApiInclGst: 26052, pendingAgeDays: 0, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 0, applicationReceivedOn: '2026-02-11T00:04:00+13:00', issuedOn: '2026-02-11T00:04:00+13:00', arrears: '11/02/2026', anniversary: '05/05/2026' } },
    '867342': { policy: { number: '867342', statusCode: 'PIF', productName: 'Business Life Cover', lifeAssuredFullName: 'Kate Rongomatane', pendingApiInclGst: 94320, pendingAgeDays: 28, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 12480, applicationReceivedOn: '2026-02-25T00:01:00+13:00', issuedOn: undefined, arrears: '25/02/2026', anniversary: '25/02/2027' } },
    '423078': { policy: { number: '423078', statusCode: 'PIF', productName: 'Umbrella Life Cover', lifeAssuredFullName: 'Chris Waititi', pendingApiInclGst: 213600, pendingAgeDays: 89, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 24360, applicationReceivedOn: '2025-12-26T00:01:00+13:00', issuedOn: undefined, arrears: '26/12/2025', anniversary: '03/04/2026' } },
    '756190': { policy: { number: '756190', statusCode: 'IF',  productName: 'Life Cover Plus', lifeAssuredFullName: 'Anna Pohatu', pendingApiInclGst: 18900, pendingAgeDays: 5, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 6240, applicationReceivedOn: '2026-03-20T00:01:00+13:00', issuedOn: '2026-03-20T00:01:00+13:00', arrears: '20/03/2026', anniversary: '20/03/2027' } },
    '381264': { policy: { number: '381264', statusCode: 'PIF', productName: 'Life Cover Plus', lifeAssuredFullName: 'Mark Tuhoe', pendingApiInclGst: 445200, pendingAgeDays: 162, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 41280, applicationReceivedOn: '2025-10-14T00:01:00+13:00', issuedOn: undefined, arrears: '14/10/2025', anniversary: '14/10/2026' } },
    '614937': { policy: { number: '614937', statusCode: 'PIF', productName: 'Umbrella Business Cover', lifeAssuredFullName: 'Grace Hapai', pendingApiInclGst: 76440, pendingAgeDays: 73, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 9840, applicationReceivedOn: '2026-01-11T00:01:00+13:00', issuedOn: undefined, arrears: '11/01/2026', anniversary: '20/04/2026' } },
    '208561': { policy: { number: '208561', statusCode: 'PIF', productName: 'Life Cover Plus', lifeAssuredFullName: 'Luke Mahaki', pendingApiInclGst: 0, pendingAgeDays: 42, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 0, applicationReceivedOn: '2026-02-11T00:01:00+13:00', issuedOn: undefined, arrears: '11/02/2026', anniversary: '11/02/2027' } },
    '935047': { policy: { number: '935047', statusCode: 'IF',  productName: 'Business Life Cover', lifeAssuredFullName: 'Sione Faleolo', pendingApiInclGst: 0, pendingAgeDays: 3, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 0, applicationReceivedOn: '2026-03-22T00:01:00+13:00', issuedOn: '2026-03-22T00:01:00+13:00', arrears: '22/03/2026', anniversary: '30/04/2026' } },
    '470823': { policy: { number: '470823', statusCode: 'PIF', productName: 'Umbrella Life Cover', lifeAssuredFullName: 'David Tuahu', pendingApiInclGst: 0, pendingAgeDays: 110, adviserName: 'Kiwi Advisory Group Ltd', adviserCode: 'KWADV1', premiumAnnualInclGst: 0, applicationReceivedOn: '2025-12-05T00:01:00+13:00', issuedOn: undefined, arrears: '05/12/2025', anniversary: '05/12/2026' } },
  };

  protected async _getPolicies(_adviserCode: string): Promise<void> {
    this.initialLoading.set(true);
    const res = this._mockDetails;
    this.initialLoading.set(false);
    const items = res.items?.map((i: BriefPolicy) => i.policy.number);
    if (items && items.length > 0) {
      this.totalPolicies.set(items.length);
      this.loadedPolicies.set(0);
      this.showProgressbar = true;
      const briefCalls = items.map((pln) =>
        this._getRowByPolicy(pln).catch(() => {})
      );
      await Promise.allSettled(briefCalls);
    }
    this.showProgressbar = false;
  }

  protected async _getRowByPolicy(policyNumber: string): Promise<void> {
    const res = this._mockBriefs[policyNumber];
    if (!res) return;
    this.loadedPolicies.update((i) => i + 1);
    if (
      res?.policy?.statusCode === 'PIF' ||
      (res?.policy?.statusCode === 'IF' && (res?.policy?.pendingAgeDays ?? 0) <= 7)
    ) {
      this.policies.update((a) => [...a, res]);
      this.filteredPolicies.update((a) => [...a, res]);
    }
  }

  getDaysTextColor(days: number): string {
    if (days <= 30) return 'cds-text-black';
    if (days >= 31 && days <= 60) return 'cds-text-custom-yellow';
    if (days >= 61) return 'cds-text-custom-orange';
    return '';
  }

  showPill(days: number): string {
    if (days <= 30) return 'cds-bg-custom-yellow';
    if (days >= 31 && days <= 60) return 'cds-bg-custom-orange';
    if (days > 60) return 'cds-bg-custom-red';
    return '';
  }

  tabClicked(ev: { label: string; value: string; index: number }) {
    this.activeTabIndex.set(ev.index);
    const tab = this.tabLabels[ev.index]?.value;
    if (tab === 'anniversary') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sixWeeksLater = new Date(today);
      sixWeeksLater.setDate(today.getDate() + 42);
      this.filteredPolicies.set(
        this.policies().filter((p) => {
          const raw = p.policy.anniversary;
          if (!raw) return false;
          const [dd, mm, yyyy] = raw.split('/').map(Number);
          const date = new Date(yyyy, mm - 1, dd);
          return date >= today && date <= sixWeeksLater;
        })
      );
    } else if (tab === 'arrears') {
      this.filteredPolicies.set(
        this.policies().filter((p) => (p.policy.pendingApiInclGst ?? 0) > 0)
      );
    } else {
      this.filteredPolicies.set(this.policies());
    }
  }

  setView(view: string) {
    this.viewType = view;
  }

   onDragStart(x: number) {
    this.startX = x;
  }

  onDragEnd(endX: number) {
    const diff = endX - this.startX;
    if (Math.abs(diff) > 60) {
      if (diff > 0) {
        this.prev();
      } else {
        this.next();
      }
    }
  }

  onTouchEnd(event: TouchEvent) {
    this.onDragEnd(event.changedTouches[0].clientX);
  }

  next() {
    this.currentIndex = Math.min(this.currentIndex + 1, this.totalSlides - 1);
  }
  prev() {
    this.currentIndex = Math.max(this.currentIndex - 1, 0);
  }

}
